export const PrivacySection = () => {
  return (
    <section id="privacy" className="scroll-mt-24">
      <h3 className="text-2xl font-semibold text-white mb-6">Privacy Policy</h3>
      <div className="prose max-w-none space-y-6 text-gray-300">
        <p className="text-gray-300">
          Last updated: January 2025
        </p>
        <p>
          Property Vue, LLC ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
        </p>
        
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-white">1. Information We Collect</h4>
            <p>We collect the following types of information:</p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><strong className="text-white">Account Information:</strong> Name, email address, and company information</li>
              <li><strong className="text-white">Payment Information:</strong> Billing details processed through Stripe (we do not store full credit card numbers)</li>
              <li><strong className="text-white">User Content:</strong> Images and other documentation you upload to the Service</li>
              <li><strong className="text-white">Usage Data:</strong> Information about how you interact with the Service, including access times and features used</li>
              <li><strong className="text-white">Support Information:</strong> Information you provide when contacting our support team via email or in-app forms</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white">2. How We Use Your Information</h4>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Provide, maintain, and improve the Property Vue Service</li>
              <li>Process payments and manage your subscription</li>
              <li>Authenticate your account and provide secure access</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send you important updates about the Service</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
              <li>Send marketing communications (with your consent, when implemented)</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white">3. Data Storage and Security</h4>
            <p>
              Your data is stored securely using industry-standard encryption and security practices. We use Supabase for secure database management and data storage, which operates on AWS infrastructure. All data is encrypted in transit and at rest.
            </p>
            <p className="mt-2">
              While we implement robust security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your data to the best of our ability.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">4. Data Sharing and Disclosure</h4>
            <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>
                <strong className="text-white">Service Providers:</strong> With trusted third-party service providers who assist us in operating the Service:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li><strong className="text-white">Supabase:</strong> Database and storage services (hosted on AWS)</li>
                  <li><strong className="text-white">Stripe:</strong> Payment processing for subscriptions</li>
                  <li><strong className="text-white">Resend:</strong> Email delivery for notifications and authentication</li>
                </ul>
              </li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with notice to users</li>
              <li><strong className="text-white">With Your Consent:</strong> When you explicitly authorize us to share your information</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white">5. Your Rights and Choices</h4>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-white">Correction:</strong> Update or correct inaccurate information in your account</li>
              <li><strong className="text-white">Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong className="text-white">Data Portability:</strong> Download and export your images at any time through the Service</li>
              <li><strong className="text-white">Opt-Out:</strong> Unsubscribe from marketing communications (service-related emails will continue)</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, please contact us at{" "}
              <a href="mailto:help@propertyvue.io" className="text-primary/80 hover:text-primary hover:underline">
                help@propertyvue.io
              </a> or through the support form in the application.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">6. Data Retention</h4>
            <p>
              We retain your personal information and User Content for as long as your account is active or as needed to provide the Service. 
            </p>
            <p className="mt-3">
              <strong className="text-white">Account Cancellation:</strong> Upon account cancellation, your data will be retained for 30 days, after which it will be permanently deleted. You may request data export before cancellation.
            </p>
            <p className="mt-3">
              <strong className="text-white">Deleted Content:</strong> Images and other content that you delete will be retained for up to 30 days before permanent deletion, allowing for recovery if needed.
            </p>
            <p className="mt-2">
              We may retain certain information for longer periods as required by law or for legitimate business purposes.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">7. Cookies and Tracking Technologies</h4>
            <p>
              Property Vue uses cookies and similar tracking technologies to authenticate users, maintain your session, and enhance your experience. You can control cookies through your browser settings, though disabling cookies may limit some functionality of the Service.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">8. Children's Privacy</h4>
            <p>
              Property Vue is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete such information promptly.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">9. International Data Transfers</h4>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence, including the United States where our service providers (Supabase, Stripe, Resend) operate. These countries may have data protection laws that differ from those in your country. By using the Service, you consent to the transfer of your information to these countries.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">10. California Privacy Rights</h4>
            <p>
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), including:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>The right to know what personal information we collect</li>
              <li>The right to delete personal information</li>
              <li>The right to opt-out of the sale of personal information (we do not sell personal information)</li>
              <li>The right to non-discrimination for exercising your privacy rights</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, please contact us at{" "}
              <a href="mailto:help@propertyvue.io" className="text-primary/80 hover:text-primary hover:underline">
                help@propertyvue.io
              </a>
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">11. Marketing Communications</h4>
            <p>
              We currently do not send marketing emails. When we implement marketing communications in the future, we will obtain your consent before sending such communications, and you will have the ability to opt-out at any time.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">12. Changes to This Privacy Policy</h4>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by email and by posting the updated policy on our website. Your continued use of the Service after such changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">13. Contact Us</h4>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact Property Vue, LLC at{" "}
              <a href="mailto:help@propertyvue.io" className="text-primary/80 hover:text-primary hover:underline">
                help@propertyvue.io
              </a> or through the support form in the application.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
